import {
  EXTEND_SUBSCRIPTION_DURATIONS,
  LanguageNameSpaces,
  PaymentMethod,
} from "@shared/types";
import { calcApiSlotsPrice } from "features/settings/Subscription/utils";
import { useUser } from "@shared/utils/hooks/misc";
import { getUserExpiration } from "@shared/features/settings/subscription/utils";
import { DURATION_TO_SECONDARY_DISPLAY } from "@shared/features/settings/subscription/constants";
import { toFixed } from "@shared/utils/numbers";
import { usePromoPrices } from "features/settings/Subscription/usePromoPrices.ts";
import { useContext, useMemo } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { joinWords } from "@shared/common/i18n/utils";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";

interface usePlanFormProps {
  paymentMethod?: PaymentMethod;
  cryptoTotalPrice?: string;
}

const usePlanForm = ({ paymentMethod, cryptoTotalPrice }: usePlanFormProps) => {
  const {
    plan,
    duration,
    promoCodePrices,
    showUpgradePrices,
    numApiSlots,
    upgradePrices,
    isCryptoPayment,
    isGetUpgradePricesEnabled,
    extraClientSlots,
  } = useContext(BuySubscriptionContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { user } = useUser();
  const expirationDate = getUserExpiration(user, duration, showUpgradePrices);

  const {
    totalPrice,
    promoCodeTotalPrice,
    initialTotalPrice,
    newPlan,
    originalPlanPrice,
    initialUpgradePrice,
    planPrice,
  } = usePromoPrices();

  const showPromoDiscount =
    promoCodePrices?.[duration] && totalPrice !== promoCodeTotalPrice;

  // planPrice is fee-free; totalPrice already includes the 5% crypto fee for the plan only
  const planPriceNoFee = showPromoDiscount
    ? promoCodePrices?.[duration]?.recurring_price ?? planPrice
    : planPrice;
  const showRenewalRow = showUpgradePrices && !isCryptoPayment;

  const getApiSlotsPrice = () => {
    let price: number | undefined;
    if (isGetUpgradePricesEnabled) {
      price = upgradePrices?.slots_setup_price;
    } else {
      const { totalPrice: rawApiSlotsPrice } = calcApiSlotsPrice(
        numApiSlots ?? 0,
        plan?.[duration]?.additional_slot_price
      );
      price = rawApiSlotsPrice * (duration / 30);
    }
    return price ?? 0;
  };

  const apiSlotsPrice = getApiSlotsPrice();
  // Use fee-free plan price as base so the 5% fee is calculated once on (plan + API slots)
  const baseTotalPrice = planPriceNoFee + apiSlotsPrice;
  const correctedExtraFee = isCryptoPayment
    ? Math.round(0.05 * baseTotalPrice * 100) / 100
    : 0;
  const finalTotalPrice = baseTotalPrice + correctedExtraFee;

  const upgradePrice =
    showUpgradePrices && upgradePrices?.upgrade_setup_price !== undefined
      ? showPromoDiscount
        ? promoCodePrices?.[duration]?.one_time_cost ??
          upgradePrices.upgrade_setup_price
        : upgradePrices.upgrade_setup_price
      : showPromoDiscount
      ? promoCodePrices?.[duration]?.one_time_cost
      : planPrice;

  const displayedDurations = useMemo(() => {
    const durations = EXTEND_SUBSCRIPTION_DURATIONS.slice().reverse();
    if (!showUpgradePrices) return durations;
    return durations.filter(
      (duration) =>
        !!user?.package_duration && duration >= user.package_duration
    );
  }, [showUpgradePrices, user]);

  if (!newPlan) return null;

  const durationSelectionData = {
    title: t("plan_form.duration"),
    show: !showUpgradePrices,
    durations: displayedDurations,
    hideRadio: displayedDurations.length === 1,
  };

  const extraFeeData = {
    title: t("plan_summary.crypto_processing_fee"),
    price: correctedExtraFee,
    show: !!correctedExtraFee && isCryptoPayment,
  };

  const totalDueToday =
    showUpgradePrices && upgradePrices !== undefined
      ? upgradePrices.upgrade_setup_price +
        upgradePrices.slots_setup_price +
        correctedExtraFee
      : finalTotalPrice;

  const totalData = {
    title: joinWords([
      t("plan_summary.total"),
      showUpgradePrices ? t("plan_summary.due_today") : "",
    ]),
    initialPrice: `$${toFixed(initialTotalPrice + apiSlotsPrice, 2)}`,
    finalPrice: `$${toFixed(
      showUpgradePrices ? totalDueToday : finalTotalPrice,
      2
    )}`,
    showInitial:
      toFixed(initialTotalPrice + apiSlotsPrice, 2) !==
      toFixed(showUpgradePrices ? totalDueToday : finalTotalPrice, 2),
  };

  const totalCryptoData = {
    show: isCryptoPayment && cryptoTotalPrice !== undefined,
    title: `${paymentMethod?.payment_option} ${t("plan_summary.to_pay")}`,
    price: parseFloat(cryptoTotalPrice || "0"),
  };

  const autoRenewalHelperProps = {
    analyticsFieldName: "auto-renewal",
    analyticsPageName: "Payment",
    description: t("plan_summary.promo_disclaimer"),
    articleId: "6758775",
  };

  const apiSlotsRecurringPayment =
    isGetUpgradePricesEnabled && upgradePrices !== undefined
      ? upgradePrices.slots_recurring_price
      : 0;
  const planRecurringPrice =
    isGetUpgradePricesEnabled && upgradePrices !== undefined
      ? upgradePrices.plan_recurring_price
      : originalPlanPrice;

  const renewalBreakdownData =
    showRenewalRow &&
    ((numApiSlots ?? 0) > 0 || (extraClientSlots ?? 0) > 0) &&
    upgradePrices !== undefined
      ? {
          planName: plan?.subscription_type,
          planPrice: planRecurringPrice,
          numApiSlots,
          apiSlotsRecurringPayment,
          totalSlots: upgradePrices.total_number_of_clients,
          baseNumberOfClients: upgradePrices.base_number_of_clients,
          existingNumberOfClients: upgradePrices.existing_number_of_clients,
        }
      : undefined;

  const nextRenewalCostData = {
    show: showRenewalRow,
    title: t("plan_summary.next_renewal_cost"),
    helperTooltipProps: autoRenewalHelperProps,
    showHelperTooltip: showPromoDiscount,
    price: planRecurringPrice + apiSlotsRecurringPayment,
    userExpirationDate: expirationDate,
    billedDuration: `${t("plan_summary.billed")} ${t(
      DURATION_TO_SECONDARY_DISPLAY[duration]
    )?.toLowerCase()}`,
    renewalBreakdownData,
  };

  const upgradeRowData = {
    show: showUpgradePrices,
    title: t("plan_summary.upgrade_to", {
      from: user?.package,
      to: plan?.subscription_type,
    }),
    initialPrice: initialUpgradePrice,
    price: upgradePrice,
  };

  return {
    extraFeeData,
    totalData,
    totalCryptoData,
    nextRenewalCostData,
    upgradeRowData,
    initialTotalPrice,
    promoCodeTotalPrice,
    durationSelectionData,
    upgradePrices,
  };
};

export default usePlanForm;
